import { apiFetch } from '../api/client';
import { downloadFileUrl } from '../fileBrowser/fileBrowserApi';
import { showErrorToast, showSuccessToast, showToast } from '../notifications/toast';
import { displayNameFromPath } from '../mediaViewer/mediaFileTypes';
import { pdfPreviewSrc, pdfViewerHref } from './pdfPreviewUrls';

export function downloadPdfFile(mountId: string, relativePath: string): void {
  if (!mountId || !relativePath) return;
  window.open(downloadFileUrl(mountId, relativePath), '_blank', 'noopener');
}

/**
 * Print a PDF from storage.
 *
 * Browsers require a user gesture for print/popups. Opening the window
 * synchronously on click, then loading the authenticated blob into it, keeps
 * that gesture so the print dialog can open after the async fetch.
 */
export async function printPdfFile(mountId: string, relativePath: string): Promise<void> {
  if (!mountId || !relativePath) return;

  // Must happen in the same turn as the button click (before any await).
  const printWindow = window.open('about:blank', '_blank');
  if (!printWindow) {
    showErrorToast('Pop-up blocked. Allow pop-ups for this site to print PDFs.');
    return;
  }

  try {
    printWindow.document.write(
      '<!DOCTYPE html><html><head><title>Print</title></head>' +
        '<body style="margin:0;font:14px system-ui,sans-serif;padding:1.5rem;color:#444">' +
        'Preparing PDF for print…</body></html>'
    );
    printWindow.document.close();
  } catch {
    /* about:blank write can fail in strict environments; continue */
  }

  let objectUrl: string | null = null;
  try {
    const res = await apiFetch(pdfPreviewSrc(mountId, relativePath));
    if (!res.ok) {
      printWindow.close();
      showErrorToast('Could not load the PDF for printing.');
      return;
    }

    const raw = await res.blob();
    if (!raw.size) {
      printWindow.close();
      showErrorToast('Could not load the PDF for printing.');
      return;
    }

    const pdfBlob =
      raw.type === 'application/pdf' ? raw : new Blob([raw], { type: 'application/pdf' });
    objectUrl = URL.createObjectURL(pdfBlob);

    // Navigate the gesture-opened window to the PDF blob. Do not rely on
    // load events after this — Chrome's PDF viewer often skips them.
    printWindow.location.href = objectUrl;

    let printed = false;
    const tryPrint = () => {
      if (printed || printWindow.closed) return;
      printed = true;
      try {
        printWindow.focus();
        printWindow.print();
      } catch {
        // Leave the tab open so the user can Ctrl+P manually.
        showToast('PDF opened in a new tab — use Print (Ctrl+P) there.', 'info');
      }
    };

    window.setTimeout(tryPrint, 800);
    window.setTimeout(tryPrint, 2000);

    window.setTimeout(() => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    }, 120_000);
  } catch {
    try {
      printWindow.close();
    } catch {
      /* ignore */
    }
    if (objectUrl) URL.revokeObjectURL(objectUrl);
    showErrorToast('Could not load the PDF for printing.');
  }
}

export async function sharePdfViewerLink(mountId: string, relativePath: string): Promise<void> {
  if (!mountId || !relativePath) return;

  const url = new URL(pdfViewerHref(mountId, relativePath), window.location.origin).href;
  const title = displayNameFromPath(relativePath);

  if (typeof navigator.share === 'function') {
    try {
      await navigator.share({ url, title });
      return;
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
    }
  }

  if (!navigator.clipboard?.writeText) {
    showErrorToast('Sharing is not supported in this browser.');
    return;
  }

  try {
    await navigator.clipboard.writeText(url);
    showSuccessToast('Link copied to clipboard.');
  } catch {
    showErrorToast('Could not copy the link.');
  }
}
